import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  type?: 'website' | 'article';
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
  };
  noindex?: boolean;
}

const SEO = ({ 
  title = "Oh Mind Guide - Where Neuroscience Meets Everyday Life",
  description = "A playful, science-driven space for keeping your brain active, flexible, and resilient. Articles, games, and exercises based on neuroscience.",
  path = "",
  type = "website",
  article,
  noindex = false
}: SEOProps) => {
  const baseUrl = "https://ohmindguide.com";
  const ogImage = `${baseUrl}/og-image.png`;
  
  // Normalize path: ensure it starts with / and handle trailing slashes consistently
  const normalizedPath = path === "" || path === "/" 
    ? "/" 
    : path.startsWith("/") ? path : `/${path}`;
  
  // Homepage gets trailing slash, other pages don't
  const canonicalUrl = normalizedPath === "/" 
    ? `${baseUrl}/` 
    : `${baseUrl}${normalizedPath.replace(/\/$/, '')}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Oh Mind Guide" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@ohmindguide" />
      
      {/* Article-specific meta tags */}
      {type === 'article' && article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.section && (
            <meta property="article:section" content={article.section} />
          )}
        </>
      )}
    </Helmet>
  );
};

export default SEO;
